import { EditableValue } from './EditableValue';
import { ReactNode } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import dayjs from 'dayjs';
import { FormHelperText, Stack } from '@mui/material';
import { DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';

interface DateTimeRange {
  startDate: Date;
  endDate: Date;
}

interface EditableDateRangeValueProps {
  editable: boolean;
  renderValue: () => ReactNode;
  onSave: (value: DateTimeRange) => void;
  defaultValue: DateTimeRange;
}

export const EditableDateRangeValue = ({
  editable,
  renderValue,
  onSave,
  defaultValue,
}: EditableDateRangeValueProps) => {
  const t = useAppTFunction();

  return (
    <EditableValue
      editable={editable}
      renderValue={renderValue}
      defaultValue={defaultValue}
      onSave={onSave}
      validate={(value) => {
        if (!value?.startDate) return t('validation.required');
        if (value?.endDate && dayjs(value?.endDate).isBefore(value?.startDate)) {
          return 'validation.endDateBeforeStart';
        }
        return undefined;
      }}
      renderInput={({ value, onChange, error }) => (
        <Stack spacing={1}>
          <DateRangePicker
            formatDensity="dense"
            value={[value?.startDate ?? null, value?.endDate ?? null]}
            onChange={(newValue: [Date | null, Date | null]) => {
              onChange({
                startDate: newValue[0] ? newValue[0] : defaultValue.startDate,
                endDate: newValue[1] ? newValue[1] : defaultValue.endDate,
              });
            }}
            slotProps={{
              textField: {
                size: 'small',
                error: !!error,
                InputProps: {
                  sx: { fontSize: '14px' },
                },
              },
            }}
            slots={{
              field: SingleInputDateRangeField,
            }}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </Stack>
      )}
    />
  );
};
